import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppStore } from '@/stores/appStore';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Landmark, Clock, Copy, Check } from 'lucide-react';

interface LocationState {
  plan: 'basic' | 'intermediate' | 'advanced';
  currency: string;
}

const regionalPricing: Record<string, Record<string, { price: number; symbol: string; code: string }>> = {
  NGN: {
    basic: { price: 2500, symbol: '₦', code: 'NGN' },
    intermediate: { price: 5000, symbol: '₦', code: 'NGN' },
    advanced: { price: 7500, symbol: '₦', code: 'NGN' },
  },
  GHS: {
    basic: { price: 30, symbol: 'GH₵', code: 'GHS' },
    intermediate: { price: 60, symbol: 'GH₵', code: 'GHS' },
    advanced: { price: 90, symbol: 'GH₵', code: 'GHS' },
  },
  KES: {
    basic: { price: 350, symbol: 'KSh', code: 'KES' },
    intermediate: { price: 700, symbol: 'KSh', code: 'KES' },
    advanced: { price: 1050, symbol: 'KSh', code: 'KES' },
  },
  ZAR: {
    basic: { price: 50, symbol: 'R', code: 'ZAR' },
    intermediate: { price: 100, symbol: 'R', code: 'ZAR' },
    advanced: { price: 150, symbol: 'R', code: 'ZAR' },
  },
};

const euroPricing: Record<string, { price: number; symbol: string; code: string }> = {
  basic: { price: 5, symbol: '€', code: 'EUR' },
  intermediate: { price: 10, symbol: '€', code: 'EUR' },
  advanced: { price: 15, symbol: '€', code: 'EUR' },
};

export default function BankTransfer() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, currentOrganization } = useAppStore();

  const state = location.state as LocationState | null;

  const [transferRef, setTransferRef] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [copiedRef, setCopiedRef] = useState(false);

  useEffect(() => {
    if (!state?.plan) {
      navigate('/subscription');
    }
  }, [state, navigate]);

  if (!state?.plan) return null;

  const getPricing = () => {
    const currency = state.currency;
    if (currency && regionalPricing[currency]?.[state.plan]) {
      return regionalPricing[currency][state.plan];
    }
    return euroPricing[state.plan];
  };

  const pricing = getPricing();

  const bankName = import.meta.env.VITE_BANK_NAME || 'Your Bank Name';
  const accountName = import.meta.env.VITE_BANK_ACCOUNT_NAME || 'Account Holder Name';
  const accountNumber = import.meta.env.VITE_BANK_ACCOUNT_NUMBER || '0000000000';
  const swiftCode = import.meta.env.VITE_BANK_SWIFT || 'XXXXXXXX';

  const suggestedRef = currentOrganization
    ? `TICKALLY-${currentOrganization.id.slice(0, 8).toUpperCase()}-${state.plan.toUpperCase()}`
    : `TICKALLY-${state.plan.toUpperCase()}`;

  const handleCopyRef = async () => {
    await navigator.clipboard.writeText(suggestedRef);
    setCopiedRef(true);
    setTimeout(() => setCopiedRef(false), 2000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentOrganization || !user) {
      toast.error('Please sign in and select an organization');
      return;
    }

    if (!transferRef.trim()) {
      toast.error('Please enter your transfer reference');
      return;
    }

    setIsSubmitting(true);

    try {
      const now = new Date();
      const expiresAt = new Date(now);
      expiresAt.setMonth(expiresAt.getMonth() + 1);

      // Insert subscription with pending_bank_transfer status
      const { data: subData, error: subError } = await supabase
        .from('subscriptions')
        .insert({
          organization_id: currentOrganization.id,
          plan: state.plan,
          status: 'pending_bank_transfer',
          amount: pricing.price,
          currency: pricing.code,
          payment_provider: 'bank_transfer',
          current_period_start: now.toISOString(),
          current_period_end: expiresAt.toISOString(),
          cancel_at_period_end: false,
        })
        .select('id')
        .single();

      if (subError) throw subError;

      // Insert bank transfer request
      const { error: reqError } = await supabase
        .from('bank_transfer_requests')
        .insert({
          organization_id: currentOrganization.id,
          subscription_id: subData.id,
          plan: state.plan,
          amount: pricing.price,
          currency: pricing.code,
          transfer_reference: transferRef.trim(),
          transfer_description: notes.trim() || null,
          submitted_by: user.id,
          status: 'pending',
        });

      if (reqError) throw reqError;

      // Update organization subscription status to pending_bank_transfer
      const { error: orgError } = await supabase
        .from('organizations')
        .update({
          subscription_plan: state.plan,
          subscription_status: 'pending_bank_transfer',
        })
        .eq('id', currentOrganization.id);

      if (orgError) throw orgError;

      setIsSuccess(true);
    } catch (error) {
      console.error('Bank transfer submission error:', error);
      toast.error('Failed to submit transfer details. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="max-w-md mx-auto py-12">
        <Card className="text-center">
          <CardContent className="pt-12 pb-8">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="w-10 h-10 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Transfer Details Submitted</h2>
            <p className="text-muted-foreground mb-4">
              Your transfer reference has been recorded. Our team will verify your payment and
              activate your subscription within <strong>1–2 business days</strong>.
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              You will be notified once your subscription is active. You can check the status on
              the pending page.
            </p>
            <div className="flex flex-col gap-2">
              <Button onClick={() => navigate('/payment/pending')}>
                View Transfer Status
              </Button>
              <Button variant="ghost" onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-8">
      <Button
        variant="ghost"
        className="mb-4"
        onClick={() => navigate('/subscription')}
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Plans
      </Button>

      <div className="grid md:grid-cols-5 gap-6">
        {/* Bank Transfer Form */}
        <div className="md:col-span-3 space-y-4">
          {/* Bank Details Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Landmark className="w-5 h-5" />
                <CardTitle>Bank Transfer Details</CardTitle>
              </div>
              <CardDescription>
                Transfer the exact amount below to activate your subscription.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="bg-muted rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Bank</span>
                  <span className="font-medium">{bankName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Account Name</span>
                  <span className="font-medium">{accountName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Account Number</span>
                  <span className="font-medium font-mono">{accountNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">SWIFT / Sort Code</span>
                  <span className="font-medium font-mono">{swiftCode}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-base">
                  <span className="text-muted-foreground">Amount to Transfer</span>
                  <span className="font-bold text-primary">
                    {pricing.symbol}{pricing.price.toLocaleString()} {pricing.code}
                  </span>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                <p className="font-medium text-amber-900 mb-1">Suggested Payment Reference</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 font-mono text-amber-800 bg-amber-100 px-2 py-1 rounded text-xs break-all">
                    {suggestedRef}
                  </code>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="shrink-0 h-7 w-7 p-0"
                    onClick={handleCopyRef}
                  >
                    {copiedRef ? (
                      <Check className="w-3 h-3 text-green-600" />
                    ) : (
                      <Copy className="w-3 h-3" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-amber-700 mt-1">
                  Use this as the payment reference when making your transfer.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Submit Reference Form */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Confirm Your Transfer</CardTitle>
              <CardDescription>
                After completing the bank transfer, enter your reference below so we can match your payment.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="transferRef">Transfer Reference <span className="text-destructive">*</span></Label>
                  <Input
                    id="transferRef"
                    value={transferRef}
                    onChange={(e) => setTransferRef(e.target.value)}
                    placeholder={suggestedRef}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter the exact reference you used when making the transfer.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Additional Notes (optional)</Label>
                  <Textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any extra information about your transfer…"
                    rows={3}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    'Submitting…'
                  ) : (
                    <>
                      <Landmark className="w-4 h-4 mr-2" />
                      Submit Transfer Details
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Order Summary */}
        <div className="md:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Order Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Plan</span>
                <span className="font-medium capitalize">{state.plan}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Billing</span>
                <span className="font-medium">Monthly</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Currency</span>
                <span className="font-medium">{pricing.code}</span>
              </div>

              <Separator />

              <div className="flex justify-between text-lg">
                <span className="font-medium">Total</span>
                <span className="font-bold">{pricing.symbol}{pricing.price}/mo</span>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm space-y-1">
                <div className="flex items-center gap-2 font-medium text-blue-900">
                  <Clock className="w-4 h-4" />
                  Manual Approval
                </div>
                <p className="text-blue-700 text-xs">
                  Your subscription will be activated within <strong>1–2 business days</strong> after we confirm your payment.
                </p>
              </div>

              <Badge variant="outline" className="w-full justify-center py-2">
                <Landmark className="w-3 h-3 mr-1" />
                Pay by Bank Transfer
              </Badge>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
